import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Cog6ToothIcon, ClockIcon, MicrophoneIcon, TrashIcon, PlayIcon, PauseIcon } from '@heroicons/react/24/outline';

function BreakSettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);
    const [audioPrompts, setAudioPrompts] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Audio upload state
    const [uploadingAudio, setUploadingAudio] = useState(false);
    const [audioForm, setAudioForm] = useState({
        name: '',
        prompt_type: 'break_start',
        file: null,
    });

    // Audio playback
    const [playingId, setPlayingId] = useState(null);
    const audioRef = React.useRef(null);

    const fetchConfig = useCallback(async () => {
        try {
            const response = await axios.get('/api/break-config/');
            const configs = response.data?.results || response.data || [];
            if (configs.length > 0) {
                setConfig(configs[0]);
            } else {
                // Create default config
                const newConfig = await axios.post('/api/break-config/', {
                    mandatory_interval_minutes: 60,
                    break_duration_minutes: 5,
                    enable_stress_checkin: true,
                    stress_checkin_interval_minutes: 30,
                    enable_audio_prompts: false,
                    strategy_reminder: "ATTACK MODE is fine as long as you're not making LIGHT CALLS. Remember: play what's in front of you, not what you wish were there.",
                });
                setConfig(newConfig.data);
            }
        } catch (err) {
            console.error('Failed to fetch break config:', err);
            setError('Failed to load break settings');
        }
    }, []);

    const fetchAudioPrompts = useCallback(async () => {
        try {
            const response = await axios.get('/api/audio-prompts/');
            setAudioPrompts(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch audio prompts:', err);
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            await Promise.all([fetchConfig(), fetchAudioPrompts()]);
            setLoading(false);
        };
        loadData();
    }, [fetchConfig, fetchAudioPrompts]);

    const handleSave = async () => {
        if (!config) return;

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await axios.patch(`/api/break-config/${config.id}/`, {
                mandatory_interval_minutes: config.mandatory_interval_minutes,
                break_duration_minutes: config.break_duration_minutes,
                enable_stress_checkin: config.enable_stress_checkin,
                stress_checkin_interval_minutes: config.stress_checkin_interval_minutes,
                enable_audio_prompts: config.enable_audio_prompts,
                strategy_reminder: config.strategy_reminder,
                presession_instructions: config.presession_instructions,
            });
            setSuccess('Settings saved successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to save config:', err);
            setError('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleAudioUpload = async (e) => {
        e.preventDefault();
        if (!audioForm.file || !audioForm.name) return;

        setUploadingAudio(true);
        setError(null);

        const formData = new FormData();
        formData.append('name', audioForm.name);
        formData.append('prompt_type', audioForm.prompt_type);
        formData.append('file', audioForm.file);

        try {
            await axios.post('/api/audio-prompts/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setAudioForm({ name: '', prompt_type: 'break_start', file: null });
            await fetchAudioPrompts();
            setSuccess('Audio prompt uploaded');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Failed to upload audio:', err);
            setError('Failed to upload audio prompt');
        } finally {
            setUploadingAudio(false);
        }
    };

    const handleDeleteAudio = async (id) => {
        if (!window.confirm('Delete this audio prompt?')) return;

        try {
            await axios.delete(`/api/audio-prompts/${id}/`);
            await fetchAudioPrompts();
        } catch (err) {
            console.error('Failed to delete audio:', err);
            setError('Failed to delete audio prompt');
        }
    };

    const handlePlayAudio = (prompt) => {
        if (playingId === prompt.id) {
            audioRef.current?.pause();
            setPlayingId(null);
        } else {
            if (audioRef.current) {
                audioRef.current.src = prompt.file;
                audioRef.current.play();
                setPlayingId(prompt.id);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-4xl">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
                <Cog6ToothIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Break Enforcement Settings
                </h1>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg">
                    {success}
                </div>
            )}

            {/* Main Settings */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <ClockIcon className="h-5 w-5" />
                    Timing Settings
                </h2>

                <div className="space-y-4">
                    {/* Mandatory Break Interval */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Mandatory Break Interval
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="interval"
                                    checked={config?.mandatory_interval_minutes === 60}
                                    onChange={() => setConfig({ ...config, mandatory_interval_minutes: 60 })}
                                    className="text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-gray-900 dark:text-white">60 minutes</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="interval"
                                    checked={config?.mandatory_interval_minutes === 90}
                                    onChange={() => setConfig({ ...config, mandatory_interval_minutes: 90 })}
                                    className="text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-gray-900 dark:text-white">90 minutes</span>
                            </label>
                        </div>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            A mandatory break will be enforced after this time without a break
                        </p>
                    </div>

                    {/* Break Duration */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Minimum Break Duration (minutes)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="30"
                            value={config?.break_duration_minutes || 5}
                            onChange={(e) => setConfig({ ...config, break_duration_minutes: parseInt(e.target.value) || 5 })}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            The recalibration checklist appears only after this time has passed
                        </p>
                    </div>

                    {/* Stress Check-in */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <label className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={config?.enable_stress_checkin || false}
                                onChange={(e) => setConfig({ ...config, enable_stress_checkin: e.target.checked })}
                                className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                            />
                            <span className="text-gray-900 dark:text-white font-medium">
                                Enable Stress Check-ins
                            </span>
                        </label>
                        <p className="mt-1 ml-8 text-sm text-gray-500 dark:text-gray-400">
                            Periodically prompt to check for tilt triggers during session
                        </p>

                        {config?.enable_stress_checkin && (
                            <div className="mt-3 ml-8">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Check-in Interval (minutes)
                                </label>
                                <input
                                    type="number"
                                    min="15"
                                    max="60"
                                    value={config?.stress_checkin_interval_minutes || 30}
                                    onChange={(e) => setConfig({ ...config, stress_checkin_interval_minutes: parseInt(e.target.value) || 30 })}
                                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pre-Session Instructions */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Pre-Session Instructions
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Shown before starting a session - reminders for table conduct and discipline
                </p>
                <textarea
                    value={config?.presession_instructions || ''}
                    onChange={(e) => setConfig({ ...config, presession_instructions: e.target.value })}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="• Do NOT engage in discussions that trigger ego..."
                />
            </div>

            {/* Strategy Reminder */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Break / Recalibration Reminder
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Shown at the end of the recalibration checklist before returning to play
                </p>
                <textarea
                    value={config?.strategy_reminder || ''}
                    onChange={(e) => setConfig({ ...config, strategy_reminder: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter your strategy reminder..."
                />
            </div>

            {/* Audio Prompts */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <MicrophoneIcon className="h-5 w-5" />
                    Audio Prompts
                </h2>

                <label className="flex items-center gap-3 mb-4">
                    <input
                        type="checkbox"
                        checked={config?.enable_audio_prompts || false}
                        onChange={(e) => setConfig({ ...config, enable_audio_prompts: e.target.checked })}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <span className="text-gray-900 dark:text-white font-medium">
                        Enable Audio Prompts
                    </span>
                </label>

                {config?.enable_audio_prompts && (
                    <>
                        {/* Existing audio prompts */}
                        {audioPrompts.length > 0 && (
                            <div className="space-y-2 mb-4">
                                {audioPrompts.map((prompt) => (
                                    <div
                                        key={prompt.id}
                                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => handlePlayAudio(prompt)}
                                                className="p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700"
                                            >
                                                {playingId === prompt.id ? (
                                                    <PauseIcon className="h-4 w-4" />
                                                ) : (
                                                    <PlayIcon className="h-4 w-4" />
                                                )}
                                            </button>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{prompt.name}</p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {prompt.prompt_type === 'break_start' ? 'Break Start' :
                                                     prompt.prompt_type === 'break_reminder' ? 'Break Reminder' :
                                                     prompt.prompt_type === 'checklist_complete' ? 'Checklist Complete' :
                                                     'Session End'}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteAudio(prompt.id)}
                                            className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload new audio */}
                        <form onSubmit={handleAudioUpload} className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Upload New Audio Prompt
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="Prompt name"
                                    value={audioForm.name}
                                    onChange={(e) => setAudioForm({ ...audioForm, name: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <select
                                    value={audioForm.prompt_type}
                                    onChange={(e) => setAudioForm({ ...audioForm, prompt_type: e.target.value })}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="break_start">Break Start</option>
                                    <option value="break_reminder">Break Reminder</option>
                                    <option value="checklist_complete">Checklist Complete</option>
                                    <option value="session_end">Session End</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    accept="audio/*"
                                    onChange={(e) => setAudioForm({ ...audioForm, file: e.target.files[0] })}
                                    className="flex-1 text-sm text-gray-600 dark:text-gray-400"
                                />
                                <button
                                    type="submit"
                                    disabled={uploadingAudio || !audioForm.file || !audioForm.name}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {uploadingAudio ? 'Uploading...' : 'Upload'}
                                </button>
                            </div>
                        </form>
                    </>
                )}

                {/* Hidden audio element for playback */}
                <audio
                    ref={audioRef}
                    onEnded={() => setPlayingId(null)}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium"
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </div>
    );
}

export default BreakSettingsPage;
